import { globalKeys } from '~/assets/data/global-keys';
import { useStorageService } from '~/composables/services/useStorageService';
import type { IGameCategory } from '~/interfaces/dto/games/response';
import { useGameStore } from '~/stores/games.store';
import { capitalizeString } from '~/utilities/data-transformers';

export default () => {
  const $storageService = useStorageService();
  const gameStore = useGameStore();
  const auth = useAuthStore();
  type PersonalisedKeys = keyof typeof globalKeys.personalisedPlayerKeys;
  type StorageType = 'Local' | 'Session';

  const getPersonalisedData = (
    key: PersonalisedKeys,
    storage: StorageType = 'Session',
  ): IGameCategory[] | [] =>
    $storageService[`get${storage}Storage`]({
      key: globalKeys.personalisedPlayerKeys[key](auth?.userId),
    }) || [];

  const setPersonalisedData = (
    key: PersonalisedKeys,
    games: IGameCategory[],
    storage: StorageType = 'Session',
  ) => {
    $storageService[`set${storage}Storage`]({
      key: globalKeys?.personalisedPlayerKeys[key](auth?.userId),
      data: games,
    });
    gameStore[`set${capitalizeString(key)}`](games);
  };

  const updatePersonalisedData = (
    key: PersonalisedKeys,
    game: IGameCategory | string,
    addGame: boolean = true,
    storage: StorageType = 'Session',
  ) => {
    let personalisedArr: any[] =
      $storageService[`get${storage}Storage`]({
        key: globalKeys.personalisedPlayerKeys[key](auth?.userId),
      }) || [];

    let includesFlag: boolean = false;
    if (typeof game === 'object') {
      includesFlag = (personalisedArr as IGameCategory[])
        .map((g) => g.id)
        .includes(game.id);
    }
    if (typeof game === 'string') {
      includesFlag = (personalisedArr as string[]).includes(game);
    }

    if (!includesFlag && addGame) personalisedArr.unshift(game);

    if (includesFlag && !addGame)
      personalisedArr = personalisedArr.filter((g) => {
        if (!!g.id) return g.id !== (game as IGameCategory).id;
        else return g !== game;
      });

    $storageService[`set${storage}Storage`]({
      key: globalKeys.personalisedPlayerKeys[key](auth?.userId),
      data: personalisedArr,
    });
    gameStore[`set${capitalizeString(key)}`](personalisedArr);
  };

  const setGameStore = () => {
    const personalisedkeys: PersonalisedKeys[] = [
      'favorites',
      'mostLiked',
      'recentlyPlayed',
      'recommended',
      'recentSearches',
    ];
    personalisedkeys.forEach((pk) => {
      const games = getPersonalisedData(
        pk,
        pk === 'recentSearches' ? 'Local' : 'Session',
      );
      if (!!games) gameStore[`set${capitalizeString(pk)}`](games);
    });
  };

  return {
    getPersonalisedData,
    setPersonalisedData,
    updatePersonalisedData,
    setGameStore,
  };
};
